/*! dom-parser-react v0.2.2 @license MIT */
import { Fragment, useMemo, createElement } from 'react';

var ELEMENT_NODE = 1;
var TEXT_NODE = 3;
var DOCUMENT_NODE = 9;
var DOCUMENT_FRAGMENT_NODE = 11;

var HTML_NAMESPACE = 'http://www.w3.org/1999/xhtml';

var parser;
var htmlParser = function (source) {
    if (!parser) {
        parser = new DOMParser
            ;
    }
    var dom = parser.parseFromString(("<!doctype html><body><template>" + source), 'text/html');
    var nodes = dom.body.firstChild.content.childNodes;
    return nodes.length === 1
        ? nodes[0]
        : { nodeType: DOCUMENT_FRAGMENT_NODE, childNodes: nodes };
};

// https://reactjs.org/docs/dom-elements.html#all-supported-html-attributes
var domPropertyRecord = {
    'accept-charset': 'acceptCharset',
    accesskey: 'accessKey',
    allowfullscreen: 'allowFullScreen',
    autocomplete: 'autoComplete',
    autofocus: 'autoFocus',
    autoplay: 'autoPlay',
    cellpadding: 'cellPadding',
    cellspacing: 'cellSpacing',
    charset: 'charSet',
    classid: 'classID',
    class: 'className',
    colspan: 'colSpan',
    contenteditable: 'contentEditable',
    contextmenu: 'contextMenu',
    controlslist: 'controlsList',
    crossorigin: 'crossOrigin',
    datetime: 'dateTime',
    enctype: 'encType',
    formaction: 'formAction',
    formenctype: 'formEncType',
    formmethod: 'formMethod',
    formnovalidate: 'formNoValidate',
    formtarget: 'formTarget',
    frameborder: 'frameBorder',
    hreflang: 'hrefLang',
    for: 'htmlFor',
    'http-equiv': 'httpEquiv',
    inputmode: 'inputMode',
    keyparams: 'keyParams',
    keytype: 'keyType',
    marginheight: 'marginHeight',
    marginwidth: 'marginWidth',
    maxlength: 'maxLength',
    mediagroup: 'mediaGroup',
    minlength: 'minLength',
    novalidate: 'noValidate',
    radiogroup: 'radioGroup',
    readonly: 'readOnly',
    rowspan: 'rowSpan',
    spellcheck: 'spellCheck',
    srcdoc: 'srcDoc',
    srclang: 'srcLang',
    srcset: 'srcSet',
    tabindex: 'tabIndex',
    usemap: 'useMap',
};

var parseStyle = function (inlineStyle) {
    var tokens = inlineStyle.trim().split(/([:;'"\\]|\/\*|\*\/)/);
    var style = {};
    var modeToken = null;
    var isEscape = false;
    var property = '';
    var value = '';
    for (var i = 0, len = tokens.length; i < len; ++i) {
        var token = tokens[i];
        if (!token)
            { continue; }
        // comment mode
        if (modeToken === '/*') {
            if (token === '*/') {
                modeToken = null;
            }
            continue;
        }
        // escaped token
        if (isEscape) {
            value += token;
            isEscape = false;
            continue;
        }
        isEscape = token === '\\';
        // css string value mode (quotation)
        if (modeToken) {
            value += token;
            if (modeToken === token) {
                modeToken = null;
            }
            continue;
        }
        if (token === ':') {
            property = value.trim()
                .replace(/^-ms-/, 'ms-')
                .replace(/-(.)/g, function (_, char) { return char.toUpperCase(); });
            value = '';
            continue;
        }
        if (token === ';') {
            style[property] = value.trim();
            property = '';
            value = '';
            continue;
        }
        if (token === '\'' || token === '"') {
            value += token;
            modeToken = token;
            continue;
        }
        if (token === '/*') {
            modeToken = token;
            continue;
        }
        // other token
        value += token;
    }
    value = value.trim();
    if (value) {
        style[property] = value;
    }
    return style;
};

var map = Array.prototype.map;
var parse = function (source, options) {
    var dom = htmlParser(source);
    return transform(dom, options);
};
var transform = function (node, options) {
    var nodeType = node.nodeType;
    if (nodeType === ELEMENT_NODE) {
        return element(node, options);
    }
    if (nodeType === DOCUMENT_NODE || nodeType === DOCUMENT_FRAGMENT_NODE) {
        return root(node, options);
    }
    if (nodeType === TEXT_NODE) {
        return text(node);
    }
    return null;
};
var element = function (node, options) {
    var isHtml = node.namespaceURI === HTML_NAMESPACE;
    var tagName = isHtml ? node.tagName.toLowerCase() : node.tagName;
    var attributes = getAttributeNames(node);
    var Component = options.components
        ? options.components[tagName] || tagName
        : tagName;
    var props = {};
    attributes.forEach(function (attr) {
        var value = node.getAttribute(attr) || '';
        if (attr === 'style') {
            props[attr] = parseStyle(value);
        }
        else {
            var property = domPropertyRecord[attr] || attr;
            props[property] = (isHtml &&
                property !== 'list' &&
                property in node &&
                typeof node[property] !== 'string')
                ? node[property]
                : value;
        }
    });
    // for SVG
    if (!isHtml) {
        props.dangerouslySetInnerHTML = { __html: node.innerHTML };
        return options.createElement(Component, props);
    }
    return options.createElement.apply(options, [ Component, props ].concat( children(node.childNodes, options) ));
};
var root = function (node, options) { return options.createElement.apply(options, [ options.Fragment, null ].concat( children(node.childNodes, options) )); };
var text = function (node) { return node.data; };
var children = function (children, options) { return map.call(children, function (child) { return transform(child, options); }).filter(Boolean); };
var getAttributeNames = function (node) {
    // for IE
    if (node.getAttributeNames == undefined) {
        return map.call(node.attributes, function (attr) { return attr.name; });
    }
    return node.getAttributeNames();
};

function DOMParserReact(props) {
    var Fragment$1 = props.Fragment || Fragment;
    var element = useMemo(function () { return (parse(props.source, {
        createElement: props.createElement || createElement,
        Fragment: Fragment$1,
        components: props.components,
    })
    // eslint-disable-next-line
    ); }, props.deps || [props.source]);
    return typeof element === 'string' ? createElement(Fragment$1, null, element) : element;
}

export { DOMParserReact as default, parse };
